/**
 * FlexX - A flexbox component for arranging children in a row or column.
 *
 * @author Koh Tong Seng <tongsengkoh@ifca.com.my>
 * @since 2023-07-12

 * @param {Object} props - The component props
 * @param {React.ReactNode} [props.children] - The children to render
 * @param {string} [props.className] - The CSS class name to apply
 * @param {AlignItemsEnum} [props.crossAxisAlignment] - The cross axis alignment
 * @param {'row' | 'column'} props.flexDirection - The flexbox direction
 * @param {boolean} [props.fullWidth] - Whether to take up the full width
 * @param {string} [props.gap] - The gap between children
 * @param {string} [props.height] - The height of the component
 * @param {JustifyContentEnum} [props.mainAxisAlignment] - The main axis alignment
 * @param {OverflowEnum} [props.overflow] - The overflow strategy
 *
 * @returns {JSX.Element} The rendered component
 */

import React from 'react'
import { AlignItemsEnum, JustifyContentEnum, OverflowEnum } from './LayoutXenum'
import useLayoutStyle from './useLayoutStyle'

type FlexXProps = {
  children?: React.ReactNode
  className?: string
  crossAxisAlignment?: AlignItemsEnum
  flexDirection: 'row' | 'column'
  fullWidth?: boolean
  gap?: string
  height?: string
  mainAxisAlignment?: JustifyContentEnum
  overflow?: OverflowEnum
}

export const FlexX = React.memo<FlexXProps>(
  ({
    children,
    className,
    crossAxisAlignment,
    flexDirection,
    fullWidth,
    gap,
    height,
    mainAxisAlignment,
    overflow,
  }: FlexXProps) => {
    const style = useLayoutStyle({
      display: 'flex',
      crossAxisAlignment,
      direction: flexDirection,
      fullWidth,
      gap,
      height,
      mainAxisAlignment,
      overflow,
    })

    return <div className={className} style={style}>{children}</div>
  }
)
